import { useMemo } from "react";
import { Clock } from "lucide-react";
import {
  hasDowntimeHours,
  roundHoursForDisplay,
  shutdownListEventHours,
} from "@/components/root-cause-analysis/root-cause-analysis-helpers.js";
import { formatStartTime } from "@/lib/datetime-utils.js";
import { Badge } from "@/components/ui/badge.jsx";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";
import { cn } from "@/lib/utils.js";
import {
  getDowntimeDurationBadgeClass,
  getDowntimeDurationDotClass,
  getDowntimeDurationTier,
  getOngoingDowntimeBadgeClass,
  getOngoingDowntimeDotClass,
} from "@/lib/downtime-duration-styles.js";

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

/** Fixed pill width; padding/weight match Alarm History pills. */
const DOWNTIME_PILL_CELL_CLASS =
  "w-[5.25rem] shrink-0 justify-center px-2 py-0.5 text-center text-[10px] tabular-nums leading-tight";

/**
 * Pill label for a closed downtime row: `"<1h"` under one hour, otherwise `roundHoursForDisplay(dtHours)` + `"h"`.
 * @param {number} dtHours - Raw `DowntimeHours` from the row
 * @returns {string} e.g. `"<1h"`, `"4h"`, `"12.5h"`
 */
function formatDowntimePillHours(dtHours) {
  const h = Number(dtHours) || 0;
  if (h < 1) return "<1h";
  return `${roundHoursForDisplay(h)}h`;
}

/**
 * Keeps shutdown rows whose `ReportingDate` falls in `[anchor − 30 days, anchor]` (inclusive).
 * @param {Array<{ ReportingDate: string }>} events - Shutdown list rows for one asset
 * @param {string} anchorIso - Selected shutdown reporting instant (ISO string)
 * @returns {Array<{ ReportingDate: string }>} Filtered rows (caller typically sorts)
 */
function filterThirtyDaysThroughAnchor(events, anchorIso) {
  const end = new Date(anchorIso);
  if (Number.isNaN(end.getTime())) return [];

  const endMs = end.getTime();
  const startMs = endMs - THIRTY_DAYS_MS;

  return events.filter((e) => {
    const t = new Date(e.ReportingDate).getTime();
    if (Number.isNaN(t)) return false;
    return t >= startMs && t <= endMs;
  });
}

/**
 * String for the panel subtitle total hours (sums numeric `DowntimeHours` only); applies `roundHoursForDisplay`.
 * @param {number} sum - Sum of hours in the visible window
 * @returns {string} e.g. `"0"`, `"42"`, `"12.5"`
 */
function formatTotalHours(sum) {
  return String(roundHoursForDisplay(sum));
}

/**
 * Row is the shutdown the user clicked when `UniqueId` matches, else same instant as anchor date.
 * @param {{ UniqueId?: number, ReportingDate?: string }} event
 * @param {number|null|undefined} selectedUniqueId
 * @param {string|null|undefined} anchorReportingDate
 */
function isSelectedHistoryRow(event, selectedUniqueId, anchorReportingDate) {
  if (selectedUniqueId != null && event.UniqueId != null) {
    return Number(event.UniqueId) === Number(selectedUniqueId);
  }
  if (anchorReportingDate && event.ReportingDate) {
    const a = new Date(anchorReportingDate).getTime();
    const b = new Date(event.ReportingDate).getTime();
    if (!Number.isNaN(a) && !Number.isNaN(b)) return a === b;
  }
  return false;
}

/**
 * 30-day downtime window ending at the selected shutdown reporting time.
 * Shares `shutdownEvents` with `ShutdownEventsList` so the RCA page performs a single `by-asset` fetch.
 * @param {{
 *   shutdownEvents: Array<{ UniqueId?: number, ReportingDate: string, RelatedAlarm?: string, DowntimeHours?: number|null }>,
 *   shutdownEventsLoading: boolean,
 *   shutdownEventsError: string|null,
 *   reportingDate: string|null,
 *   selectedUniqueId?: number|null,
 * }} props - `reportingDate` is ISO from the selected shutdown row when set.
 * @returns {JSX.Element}
 */
export default function DowntimeHistoryList({
  shutdownEvents,
  shutdownEventsLoading,
  shutdownEventsError,
  reportingDate,
  selectedUniqueId = null,
}) {
  const windowRows = useMemo(() => {
    if (
      !reportingDate
      || !Array.isArray(shutdownEvents)
      || shutdownEvents.length === 0
    )
      return [];
    const inWindow = filterThirtyDaysThroughAnchor(
      shutdownEvents,
      reportingDate
    );
    return [...inWindow].sort(
      (a, b) =>
        new Date(b.ReportingDate).getTime()
        - new Date(a.ReportingDate).getTime()
    );
  }, [shutdownEvents, reportingDate]);

  const { eventCount, totalHours } = useMemo(() => {
    let sum = 0;
    for (const row of windowRows) {
      if (
        row.DowntimeHours != null
        && row.DowntimeHours !== ""
        && !Number.isNaN(Number(row.DowntimeHours))
      ) {
        sum += Number(row.DowntimeHours);
      }
    }
    return { eventCount: windowRows.length, totalHours: sum };
  }, [windowRows]);

  const showPlaceholder = !reportingDate;
  const showBody =
    reportingDate != null && !shutdownEventsLoading && !shutdownEventsError;

  return (
    <div className='flex h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden rounded-lg border border-border bg-card'>
      <div className='shrink-0 border-b border-border px-3 py-3 sm:px-4'>
        <div className='flex items-start gap-2'>
          <Clock
            className='mt-0.5 h-5 w-5 shrink-0 text-primary'
            aria-hidden
          />
          <div className='min-w-0'>
            <h2 className='text-base font-semibold leading-tight text-foreground'>
              Downtime History - 30 days
            </h2>
            <p className='mt-1 text-xs text-muted-foreground'>
              {showPlaceholder
                ? "Select a shutdown event to view the prior 30 days"
                : shutdownEventsLoading && reportingDate
                  ? "Loading…"
                  : shutdownEventsError && reportingDate
                    ? shutdownEventsError
                    : `${eventCount} events, ${formatTotalHours(totalHours)}h total — identifies repeat failures`}
            </p>
          </div>
        </div>
      </div>

      <div className='flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-muted/50'>
        <div className='flex min-h-0 flex-1 flex-col overflow-y-auto px-3 py-3 sm:px-4 sm:py-4'>
          {showPlaceholder ? (
            <p className='shrink-0 text-sm text-muted-foreground'>
              Shut down dates and duration for this compressor appear here after
              you choose an event from the list.
            </p>
          ) : null}

          {reportingDate && shutdownEventsLoading ? (
            <LoadingSkeleton className='min-h-[12rem] w-full shrink-0' />
          ) : null}

          {reportingDate && shutdownEventsError ? (
            <p className='shrink-0 text-sm text-red-600 dark:text-red-400'>
              {shutdownEventsError}
            </p>
          ) : null}

          {showBody && windowRows.length === 0 ? (
            <p className='shrink-0 text-sm text-muted-foreground'>
              No downtime events in this window.
            </p>
          ) : null}

          {showBody && windowRows.length > 0 ? (
            <table
              className='w-full border-collapse text-sm'
              aria-label='Downtime history in the selected 30-day window'>
              <caption className='sr-only'>
                Shut down events up to thirty days before the selected shutdown,
                most recent first
              </caption>
              <tbody className='divide-y divide-border'>
                {windowRows.map((event, index) => {
                  const dtHours = shutdownListEventHours(event);
                  const durationTier = getDowntimeDurationTier(dtHours);
                  const alarm = (event.RelatedAlarm || "").trim() || "—";
                  const key =
                    event.UniqueId != null
                      ? String(event.UniqueId)
                      : `hist-${event.ReportingDate}-${index}`;

                  const isSelected = isSelectedHistoryRow(
                    event,
                    selectedUniqueId,
                    reportingDate
                  );

                  return (
                    <tr key={key} className='align-middle'>
                      <td
                        className={cn(
                          "w-px whitespace-nowrap py-2.5 pl-2.5 pr-3 align-middle text-xs font-medium tabular-nums text-foreground",
                          isSelected && "bg-muted"
                        )}>
                        {formatStartTime(event.ReportingDate, "—")}
                      </td>
                      <td
                        className={cn(
                          "w-px py-2.5 pl-2 pr-3 align-middle text-center",
                          isSelected && "bg-muted"
                        )}>
                        <div className='flex shrink-0 justify-center'>
                          {hasDowntimeHours(event) ? (
                            <Badge
                              variant='status'
                              className={getDowntimeDurationBadgeClass(
                                durationTier,
                                DOWNTIME_PILL_CELL_CLASS
                              )}>
                              <span
                                className={getDowntimeDurationDotClass(
                                  durationTier,
                                  "h-1.5 w-1.5"
                                )}
                                aria-hidden='true'
                              />
                              {formatDowntimePillHours(dtHours)}
                            </Badge>
                          ) : (
                            <Badge
                              variant='status'
                              className={getOngoingDowntimeBadgeClass(
                                DOWNTIME_PILL_CELL_CLASS
                              )}>
                              <span
                                className={getOngoingDowntimeDotClass(
                                  "h-1.5 w-1.5"
                                )}
                                aria-hidden='true'
                              />
                              Ongoing
                            </Badge>
                          )}
                        </div>
                      </td>
                      <td
                        className={cn(
                          "min-w-0 py-2.5 pr-2.5 align-middle text-sm leading-snug text-foreground",
                          isSelected && "bg-muted"
                        )}>
                        <p className='line-clamp-2 break-words'>{alarm}</p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : null}
        </div>
      </div>
    </div>
  );
}
